import { useEffect, useRef } from 'react'
import axios from 'axios'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { generateAIImage } from 'pages/AICreation/AICreationFlow/api'
import {
  pollJobStatusUntilDone,
  shouldRouteTemplateToAiWorkflows,
} from 'pages/AICreation/AICreationFlow/AICreationFlow.utils'
import {
  type ExperimentManager,
  useExperimentManager,
} from 'services/ExperimentManager/ExperimentManager'
import { useTilesStore, usePhotoBankStore } from 'stores/RootStoreProvider'
import {
  AIGenPhotoTileStore,
  type AiTileSource,
} from 'stores/tilesStore/tiles/AIGenPhotoTileStore'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { getAmplitudeDeviceId } from 'services/Analytics/platforms/amplitude'
import { logger } from 'services/logger'
import { SNACKBAR_VARIANTS } from 'elements/Snackbar/Snackbar.consts'
import { CLOUD_SERVICES } from 'components/CloudTileUploader/CloudTileUploader.consts'
import { UPLOAD_SRC } from 'components/PhotoBank/PhotoBank.consts'
import { resolvePhotoBankSource } from '../resolvePhotoBankSource'

type TilesStore = ReturnType<typeof useTilesStore>
type PhotoBankStore = ReturnType<typeof usePhotoBankStore>
type EnqueueSnackbar = ReturnType<typeof useSnackbar>['enqueueSnackbar']
type Translate = ReturnType<typeof useTranslation>['t']

type ResolvedSource = { url: string; width: number; height: number }

type TaskParams = {
  tileId: string
  tilesStore: TilesStore
  photoBankStore: PhotoBankStore
  experimentManager: ExperimentManager
  enqueueSnackbarRef: { current: EnqueueSnackbar }
  tRef: { current: Translate }
  signal: AbortSignal
  startedAt: number
}

function durationInSeconds(startedAt: number): number {
  return Math.round((Date.now() - startedAt) / 1000)
}

async function resolveSources(
  photoBankStore: PhotoBankStore,
  sources: AiTileSource[] = []
): Promise<ResolvedSource[]> {
  const resolved = await Promise.all(
    sources.map(({ uid }) => resolvePhotoBankSource(photoBankStore, uid))
  )
  if (resolved.some((source) => !source)) {
    throw new Error('Pet variation source photo not found in photobank')
  }
  return resolved as ResolvedSource[]
}

function showFailureToast({
  enqueueSnackbarRef,
  tRef,
  isDailyLimitReached,
}: {
  enqueueSnackbarRef: { current: EnqueueSnackbar }
  tRef: { current: Translate }
  isDailyLimitReached: boolean
}) {
  const message = tRef.current(
    isDailyLimitReached
      ? 'ai_templates.errors.rate_limit'
      : 'ai_templates.variations.generation_failed'
  )
  enqueueSnackbarRef.current(message, {
    variant: SNACKBAR_VARIANTS.COMMON,
    name: EVENT_NAMES.PET_VARIATION_GENERATION_FAILED,
    title: message,
    preventDuplicate: true,
  })
}

function handleTerminalFailure({
  tile,
  jobId,
  error,
  durationStartedAt,
  tilesStore,
  enqueueSnackbarRef,
  tRef,
}: {
  tile: AIGenPhotoTileStore
  jobId: string | undefined
  error: unknown
  durationStartedAt: number
  tilesStore: TilesStore
  enqueueSnackbarRef: { current: EnqueueSnackbar }
  tRef: { current: Translate }
}) {
  const templateSlug = tile.aiTemplate.templateId ?? ''
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  const responseError = axios.isAxiosError(error)
    ? (error.response?.data as { error?: string } | undefined)?.error
    : undefined
  const errorMessage =
    responseError ?? (error instanceof Error ? error.message : undefined)
  analytics.track(EVENT_NAMES.PET_VARIATION_GENERATION_FAILED, {
    'Template Slug': templateSlug,
    'Job ID': jobId,
    'Generation Duration Ms': Date.now() - durationStartedAt,
    'Variant Index': tile.aiTemplate.generationVariantIndex,
    'Template Type': tile.aiTemplate.templateType,
    'Template Name': tile.aiTemplate.templateName,
    'Error Status': status,
    'Error Message': errorMessage,
  })
  tilesStore.removeTile(tile.id)
  analytics.track(EVENT_NAMES.PET_VARIATION_TILE_AUTO_REMOVED, {
    'Template Slug': templateSlug,
    'Job ID': jobId,
    'Variant Index': tile.aiTemplate.generationVariantIndex,
    'Template Type': tile.aiTemplate.templateType,
    'Template Name': tile.aiTemplate.templateName,
  })
  showFailureToast({
    enqueueSnackbarRef,
    tRef,
    isDailyLimitReached: status === 429,
  })
  logger.error('Pet variation generation failed', error, {
    templateSlug,
    jobId,
  })
}

async function uploadResult(
  photoBankStore: PhotoBankStore,
  result: { url: string; width: number; height: number }
): Promise<string> {
  const { photos } = await photoBankStore.uploadPhotos({
    cloudServiceName: CLOUD_SERVICES.REMOTE,
    files: [result],
    onUploadFailed: (uid) =>
      logger.error('Pet variation result upload failed', { uid }),
    uploadSrc: UPLOAD_SRC.AI_RESULT,
    withFaceDetection: false,
    filterSmallFaces: false,
  })
  const uid = photos[0]?.uid
  if (!uid) {
    throw new Error('Pet variation result upload produced no uid')
  }
  return uid
}

async function completeJob({
  tile,
  templateSlug,
  jobId,
  jobStartedAt,
  photoBankStore,
  signal,
}: {
  tile: AIGenPhotoTileStore
  templateSlug: string
  jobId: string
  jobStartedAt: number
  photoBankStore: PhotoBankStore
  signal: AbortSignal
}) {
  const { urls, width, height } = await pollJobStatusUntilDone(jobId, signal)
  signal.throwIfAborted()

  const resultUrl = urls[0]?.url
  if (!resultUrl) {
    throw new Error('Pet variation generation returned no image')
  }

  const uid = await uploadResult(photoBankStore, {
    url: resultUrl,
    width,
    height,
  })
  signal.throwIfAborted()

  tile.completeGeneration({
    uid,
    jobId,
    totalDurationSeconds: durationInSeconds(jobStartedAt),
  })
  analytics.track(EVENT_NAMES.PET_VARIATION_GENERATION_GENERATED, {
    'Template Slug': templateSlug,
    'Job ID': jobId,
    'Generation Duration Ms': Date.now() - jobStartedAt,
    'Variant Index': tile.aiTemplate.generationVariantIndex,
    'Template Type': tile.aiTemplate.templateType,
    'Template Name': tile.aiTemplate.templateName,
  })
}

async function runAiTileGenerationTask(params: TaskParams) {
  const {
    tileId,
    tilesStore,
    photoBankStore,
    experimentManager,
    enqueueSnackbarRef,
    tRef,
    signal,
    startedAt,
  } = params
  const tile = tilesStore.getTile(tileId)
  if (!(tile instanceof AIGenPhotoTileStore)) {
    throw new Error('AI tile generation task mounted for a non-AI tile')
  }

  const templateSlug = tile.aiTemplate.templateId
  if (!templateSlug) {
    throw new Error('AI tile generation task is missing a template id')
  }

  try {
    const generationStatus = tile.generationStatus
    if (!generationStatus) return

    if (generationStatus.status === 'generating') {
      await completeJob({
        tile,
        templateSlug,
        jobId: generationStatus.jobId,
        jobStartedAt: generationStatus.startedAt,
        photoBankStore,
        signal,
      })
      return
    }

    const variantIndex = tile.aiTemplate.generationVariantIndex
    const shouldUseAiWorkflows = shouldRouteTemplateToAiWorkflows(
      experimentManager,
      templateSlug
    )
    const resolvedSources = await resolveSources(photoBankStore, tile.sources)
    signal.throwIfAborted()

    const generationParams = {
      templateSlug,
      imageUrls: resolvedSources.map((source) => source.url),
      imageDimensions: resolvedSources.map(
        (source) => [source.width, source.height] as [number, number]
      ),
      amplitudeDeviceId: getAmplitudeDeviceId(),
      variantIndex,
      useAiWorkflows: shouldUseAiWorkflows,
      productFlow: 'GENERATIVE_PLUS',
    }

    const { jobId } = await generateAIImage(generationParams)
    signal.throwIfAborted()

    tile.markGenerationStarted(jobId)
    analytics.track(EVENT_NAMES.PET_VARIATION_GENERATION_STARTED, {
      'Template Slug': templateSlug,
      'Job ID': jobId,
      'Variant Index': variantIndex,
      'Template Type': tile.aiTemplate.templateType,
      'Template Name': tile.aiTemplate.templateName,
    })

    const currentStatus = tile.generationStatus
    if (currentStatus?.status !== 'generating') return
    await completeJob({
      tile,
      templateSlug,
      jobId,
      jobStartedAt: currentStatus.startedAt,
      photoBankStore,
      signal,
    })
  } catch (error) {
    if (signal.aborted) return
    const currentTile = tilesStore.tryGetTile(tileId)
    if (!(currentTile instanceof AIGenPhotoTileStore)) return
    if (!currentTile.generationStatus) return
    const jobId =
      currentTile.generationStatus.status === 'generating'
        ? currentTile.generationStatus.jobId
        : undefined
    const durationStartedAt =
      currentTile.generationStatus.status === 'generating'
        ? currentTile.generationStatus.startedAt
        : startedAt
    handleTerminalFailure({
      tile: currentTile,
      jobId,
      error,
      durationStartedAt,
      tilesStore,
      enqueueSnackbarRef,
      tRef,
    })
  }
}

export function AiTileGenerationTask({ tileId }: { tileId: string }) {
  const tilesStore = useTilesStore()
  const photoBankStore = usePhotoBankStore()
  const experimentManager = useExperimentManager()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  // t / enqueueSnackbar change identity across renders; read them via refs so the
  // generation effect runs once per tile and isn't aborted/restarted mid-flight.
  const enqueueSnackbarRef = useRef(enqueueSnackbar)
  enqueueSnackbarRef.current = enqueueSnackbar
  const tRef = useRef(t)
  tRef.current = t

  useEffect(() => {
    const controller = new AbortController()

    runAiTileGenerationTask({
      tileId,
      tilesStore,
      photoBankStore,
      experimentManager,
      enqueueSnackbarRef,
      tRef,
      signal: controller.signal,
      startedAt: Date.now(),
    })

    return () => {
      controller.abort()
    }
  }, [experimentManager, photoBankStore, tileId, tilesStore])

  return null
}
